// ============================================================
// shiels-custom.js (Shiels + Grahams + Watch Depot)
// - Shiels: full checkout guards (Rules 1–4) + /cart/add interceptor
// - Grahams / Watch Depot: Rule 1 + removal UI (data-removing, drawer delegation)
// ============================================================

(() => {
  const isShiels = !!window.STORE_CONTEXT?.isShiels;
  const isGrahams = !!window.STORE_CONTEXT?.isGrahams;
  const isWatchdepot = !!window.STORE_CONTEXT?.isWatchdepot;
  if (!isShiels && !isGrahams && !isWatchdepot) return;

  // ------------------------------------------------------------
  // 0) PATCH: install methods on CartDrawer.prototype (not instance)
  //    This is what makes it work even when code is in a separate file.
  // ------------------------------------------------------------
  function installOnCartDrawerPrototype() {
    if (typeof CartDrawer !== "function") return false;

    const proto = CartDrawer.prototype;

    // Prevent re-installing
    if (proto._shielsCheckoutGuardsInstalled) return true;
    proto._shielsCheckoutGuardsInstalled = true;

    // -----------------------------------------------------------------------
    // Core helpers (Rules 1-4 dependencies)
    // -----------------------------------------------------------------------

    /**
     * True when drawer has exactly 1 main product (no add-ons).
     */
    proto._isSingleItemCart = function () {
      if (!this.container) return false;
      const mainItems = this.container.querySelectorAll(
        '.product-cart-item:not([data-is-addon="true"]):not(.cart-item-addon)'
      );
      return mainItems.length === 1;
    };

    /**
     * True when drawer has exactly 2 items: 1 main + 1 add-on.
     */
    proto._isTwoItemsMainPlusAddon = function () {
      if (!this.container) return false;
      const all = this.container.querySelectorAll(".product-cart-item");
      if (all.length !== 2) return false;

      const mainItems = this.container.querySelectorAll(
        '.product-cart-item:not([data-is-addon="true"]):not(.cart-item-addon)'
      );
      const addOnItems = this.container.querySelectorAll(
        '.product-cart-item[data-is-addon="true"], .product-cart-item.cart-item-addon'
      );
      return mainItems.length === 1 && addOnItems.length === 1;
    };

    /**
     * True when drawer has exactly 2 main products (no add-ons). Used for checkout rule 3.
     */
    proto._isTwoMainItemsOnly = function () {
      if (!this.container) return false;
      const mainItems = this.container.querySelectorAll(
        '.product-cart-item:not([data-is-addon="true"]):not(.cart-item-addon)'
      );
      const addOnItems = this.container.querySelectorAll(
        '.product-cart-item[data-is-addon="true"], .product-cart-item.cart-item-addon'
      );
      return mainItems.length === 2 && addOnItems.length === 0;
    };

    /**
     * True when drawer has 3+ items (any mix). Used for checkout rule 4.
     */
    proto._isRule4Scenario = function () {
      if (!this.container) return false;
      const all = this.container.querySelectorAll(".product-cart-item");
      return all.length >= 3;
    };

    /**
     * Rule 4: Line numbers (1-based) that are in "removing" state and must not go to checkout.
     * Uses data-removing="true" and _cartDrawerRule4PendingRemovalLines (persisted when DOM is replaced).
     */
    proto._rule4LinesToRemove = function (drawer) {
      if (!drawer) return [];
      const lines = new Set();

      function addLineFromEl(el) {
        var row = el;
        if (!row.classList || !row.classList.contains("product-cart-item")) {
          row = row.closest && row.closest(".product-cart-item");
        }
        if (!row) return;
        var id = row.id || "";
        var m = id.match(/CartDrawerItem-(\d+)/);
        if (m) lines.add(parseInt(m[1], 10));
      }

      drawer.querySelectorAll('.product-cart-item[data-removing="true"]').forEach(addLineFromEl);
      drawer.querySelectorAll('[data-removing="true"]').forEach(function (el) {
        addLineFromEl(el);
      });

      if (window._cartDrawerRule4PendingRemovalLines && window._cartDrawerRule4PendingRemovalLines.size) {
        window._cartDrawerRule4PendingRemovalLines.forEach(function (line) {
          if (line >= 1 && drawer.querySelector("#CartDrawerItem-" + line)) {
            lines.add(line);
          }
        });
      }

      return Array.from(lines)
        .filter(function (n) {
          return n >= 1;
        })
        .sort(function (a, b) {
          return b - a;
        });
    };

    /**
     * Get add-on line indices (1-based) for a main at mainProductLine from existing cart data.
     * Same logic as getAddOnIndicesForMain but no fetch – avoids race with removeLineWithAddonIfMain.
     */
    proto._getAddOnIndicesForMainFromCart = function (cartData, mainProductLine) {
      if (!cartData || !cartData.items || cartData.items.length === 0) return [];
      const mainIndex = parseInt(mainProductLine, 10) - 1;
      if (mainIndex < 0 || mainIndex >= cartData.items.length) return [];
      const mainItem = cartData.items[mainIndex];
      if (!mainItem) return [];

      const mainKey = mainItem.key ? String(mainItem.key) : null;
      const mainVariantId = mainItem.id ? String(mainItem.id).trim() : null;
      const variantMatch = function (val) {
        return val != null && mainVariantId && String(val).trim() === mainVariantId;
      };

      const addOns = [];
      for (let i = 0; i < cartData.items.length; i++) {
        if (i === mainIndex) continue;
        const item = cartData.items[i];

        if (
          mainKey &&
          item.properties &&
          item.properties._tpo_main_product_key &&
          String(item.properties._tpo_main_product_key) === mainKey
        ) {
          addOns.push(i + 1);
          continue;
        }

        if (i === mainIndex - 1 && item.properties && variantMatch(item.properties._tpo_main_product_id)) {
          addOns.push(i + 1);
          continue;
        }

        if (
          i === mainIndex + 1 &&
          item.properties &&
          variantMatch(item.properties._tpo_main_product_id) &&
          item.properties._tpo_main_product_key &&
          String(item.properties._tpo_main_product_key) === mainKey
        ) {
          addOns.push(i + 1);
        }
      }
      return addOns;
    };

    /**
     * Rule 4: Expand list of lines to remove with add-on line numbers for any main product in the list.
     * If cartData is provided, uses it (single snapshot) to avoid race with removeLineWithAddonIfMain.
     * Returns a Promise that resolves to the full list (unique, sorted descending).
     */
    proto._rule4ExpandWithAddons = function (drawer, linesToRemove, cartData) {
      if (!drawer || !linesToRemove || linesToRemove.length === 0) {
        return Promise.resolve(linesToRemove);
      }

      // Prefer "this" (works even if window.cartDrawerInstance is not yet set)
      const instance = this;
      if (!instance || !instance.isAddonItem) return Promise.resolve(linesToRemove);

      const allLines = new Set(linesToRemove);

      // Prefer snapshot cartData to avoid race
      if (cartData && instance._getAddOnIndicesForMainFromCart) {
        linesToRemove.forEach(function (line) {
          var el = drawer.querySelector("#CartDrawerItem-" + line);
          if (!el || instance.isAddonItem(el)) return;

          var addOnLines = instance._getAddOnIndicesForMainFromCart(cartData, line);
          if (addOnLines) addOnLines.forEach(function (n) { if (n >= 1) allLines.add(n); });
        });

        return Promise.resolve(
          Array.from(allLines).sort(function (a, b) {
            return b - a;
          })
        );
      }

      // fallback: use existing method if theme provides
      if (!instance.getAddOnIndicesForMain) return Promise.resolve(linesToRemove);

      const promises = linesToRemove.map(function (line) {
        var el = drawer.querySelector("#CartDrawerItem-" + line);
        if (!el || instance.isAddonItem(el)) return Promise.resolve([]);
        return instance.getAddOnIndicesForMain(line).then(function (addOnLines) {
          return addOnLines || [];
        });
      });

      return Promise.all(promises).then(function (results) {
        results.forEach(function (arr) {
          arr.forEach(function (n) { if (n >= 1) allLines.add(n); });
        });
        return Array.from(allLines).sort(function (a, b) {
          return b - a;
        });
      });
    };

    // -----------------------------------------------------------------------
    // Document-level delegation (remove + qty +/- survive drawer refresh)
    // -----------------------------------------------------------------------
    proto.setupDrawerDocumentDelegation = function () {
      if (this._drawerDelegationBound) return;
      this._drawerDelegationBound = true;

      document.addEventListener(
        "click",
        function drawerDelegationClick(e) {
          const drawer = document.getElementById("Cart-Drawer");
          if (!drawer || !drawer.contains(e.target)) return;

          const removeBtn = e.target.closest && e.target.closest(".remove");
          if (removeBtn) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            if (window._cartDrawerRemovalInProgress) return;

            const line =
              removeBtn.getAttribute("data-index") ||
              (function () {
                const href =
                  removeBtn.getAttribute("href") ||
                  removeBtn.getAttribute("data-original-href");
                if (href && href.indexOf("line=") !== -1) {
                  const m = href.match(/line=(\d+)/);
                  return m ? m[1] : null;
                }
                return null;
              })();

            if (line && window.cartDrawerInstance) {
              const lineEl = drawer.querySelector("#CartDrawerItem-" + line);

              let productUrl = null;
              if (lineEl) {
                productUrl =
                  lineEl.getAttribute("data-product-url") ||
                  (lineEl.querySelector("a.cart-product-link") &&
                    lineEl.querySelector("a.cart-product-link").href) ||
                  (lineEl.getAttribute("data-product-handle")
                    ? "/products/" + lineEl.getAttribute("data-product-handle")
                    : null);

                lineEl.setAttribute("data-removing", "true");
                lineEl.classList.add("cart-item-removing");
                lineEl.style.pointerEvents = "none";
              }

              window.cartDrawerInstance._removedProductUrl = productUrl;

              if (lineEl && !window.cartDrawerInstance.isAddonItem(lineEl)) {
                window._cartDrawerRemovalInProgress = true;
              }

              // Rule 3: persist pending removal
              if (
                window.cartDrawerInstance._isTwoMainItemsOnly &&
                window.cartDrawerInstance._isTwoMainItemsOnly() &&
                lineEl &&
                !window.cartDrawerInstance.isAddonItem(lineEl)
              ) {
                window._cartDrawerRule3PendingRemovalLine = parseInt(line, 10);
              }

              // Rule 4: persist pending removal set
              if (
                window.cartDrawerInstance._isRule4Scenario &&
                window.cartDrawerInstance._isRule4Scenario() &&
                lineEl &&
                !window.cartDrawerInstance.isAddonItem(lineEl)
              ) {
                window._cartDrawerRule4PendingRemovalLines =
                  window._cartDrawerRule4PendingRemovalLines || new Set();
                window._cartDrawerRule4PendingRemovalLines.add(parseInt(line, 10));
              }

              window.cartDrawerInstance.removeLineWithAddonIfMain(line);

              if (window.cartDrawerInstance._updateCheckoutButtonRule1Blocked) {
                window.cartDrawerInstance._updateCheckoutButtonRule1Blocked();
              }
            }
            return;
          }

          // +/- qty handling
          const minusBtn = e.target.closest && e.target.closest(".minus");
          const plusBtn = e.target.closest && e.target.closest(".plus");
          const qtyWrapper =
            (minusBtn && minusBtn.closest("quantity-selector")) ||
            (plusBtn && plusBtn.closest("quantity-selector"));

          if (qtyWrapper) {
            const input =
              qtyWrapper.querySelector &&
              qtyWrapper.querySelector('input.qty, input[name="updates[]"]');
            if (input) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              // Force step=1 in drawer so quantity-selector and any other code see +1/-1 only
              input.setAttribute("step", "1");
              const step = 1;
              const min = parseInt(input.getAttribute("min"), 10) || 1;
              const max = parseInt(input.getAttribute("max"), 10) || Infinity;

              let val = parseInt(input.value, 10) || min;
              if (minusBtn) val = Math.max(min, val - step);
              if (plusBtn) val = Math.min(max, val + step);

              input.value = val;
              input.setAttribute("value", val);
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }
        },
        true
      );
    };

    // -----------------------------------------------------------------------
    // Checkout guard methods
    // -----------------------------------------------------------------------
    proto._updateCheckoutButtonRule1Blocked = function () {
      const drawer = document.getElementById("Cart-Drawer");
      const cartPage = document.getElementById("Cart");
      const instance = window.cartDrawerInstance || this;

      const drawerBlocked = !!(drawer && instance && instance._shouldBlockCheckout(drawer));
      const drawerBtn =
        drawer &&
        drawer.querySelector('form[data-cart-drawer-checkout-form] button[name="checkout"]');

      if (drawerBtn) {
        if (drawerBlocked) drawerBtn.classList.add("cart-drawer-checkout--blocked");
        else drawerBtn.classList.remove("cart-drawer-checkout--blocked");
      }

      const cartPageBlocked = !!(
        cartPage &&
        (cartPage.classList.contains("cart-disabled") ||
          cartPage.querySelector(".cart__row.loading") ||
          cartPage.querySelector(".product-cart-item.thb-loading") ||
          cartPage.querySelector('[data-removing="true"]'))
      );

      const cartPageBtn = document.querySelector(
        'button[name="checkout"][form="cart-form"], #cart-form ~ .proceed-to-checkout button[name="checkout"], .thb-cart-form button.checkout-button'
      );

      if (cartPageBtn) {
        if (cartPageBlocked) cartPageBtn.classList.add("cart-drawer-checkout--blocked");
        else cartPageBtn.classList.remove("cart-drawer-checkout--blocked");
      }
    };

    proto._shouldBlockCheckout = function (drawer) {
      if (!drawer) return false;
      return !!(
        window._cartDrawerRemovalInProgress ||
        drawer.querySelector('[data-removing="true"]') ||
        drawer.querySelector(".product-cart-item.thb-loading")
      );
    };

    /**
     * Rule 3: Two main items only. If exactly one has data-removing (or pending removal), remove it then checkout.
     * Returns {action:'block'|'allow'|'remove-then-checkout', line?}
     */
    proto._rule3CheckoutAction = function (drawer, instance) {
      if (!drawer || !instance || !instance._isTwoMainItemsOnly) return null;

      if (!instance._isTwoMainItemsOnly()) {
        if (window._cartDrawerRule3PendingRemovalLine != null) window._cartDrawerRule3PendingRemovalLine = null;
        return null;
      }

      const mainRemoving = drawer.querySelectorAll(
        '.product-cart-item:not([data-is-addon="true"]):not(.cart-item-addon)[data-removing="true"]'
      );

      if (mainRemoving.length === 2) return { action: "block" };

      if (mainRemoving.length === 1) {
        const id = mainRemoving[0].id || "";
        const match = id.match(/CartDrawerItem-(\d+)/);
        const line = match ? parseInt(match[1], 10) : 0;
        if (line) return { action: "remove-then-checkout", line: line };
      }

      if (mainRemoving.length === 0) {
        const pending = window._cartDrawerRule3PendingRemovalLine;
        if (pending != null && pending >= 1 && drawer.querySelector("#CartDrawerItem-" + pending)) {
          return { action: "remove-then-checkout", line: pending };
        }
        if (window._cartDrawerRule3PendingRemovalLine != null) window._cartDrawerRule3PendingRemovalLine = null;
        return { action: "allow" };
      }

      return null;
    };

    proto.setupCheckoutSubmitGuard = function () {
      if (window._cartDrawerCheckoutGuardBound) return;
      window._cartDrawerCheckoutGuardBound = true;

      const cartChangeUrl = "/cart/change.js";
      const checkoutUrl =
        typeof theme !== "undefined" && theme.routes && theme.routes.root_url
          ? theme.routes.root_url.replace(/\/?$/, "") + "/checkout"
          : "/checkout";

      function applyRule3ThenMaybeBlock(e, btn) {
        const drawer = document.getElementById("Cart-Drawer");
        const instance = window.cartDrawerInstance;
        const r3 = instance && instance._rule3CheckoutAction && instance._rule3CheckoutAction(drawer, instance);
        if (!r3) return false;
        if (r3.action === "allow") return false;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        instance._updateCheckoutButtonRule1Blocked && instance._updateCheckoutButtonRule1Blocked();
        if (r3.action === "block") return true;

        if (r3.action === "remove-then-checkout" && r3.line) {
          if (window._cartDrawerRule3PendingRemovalLine !== undefined) window._cartDrawerRule3PendingRemovalLine = null;

          fetch(cartChangeUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ line: r3.line, quantity: 0 }),
          })
            .then(function (res) { return res.json ? res.json() : res.text(); })
            .then(function () { window.location.href = checkoutUrl; })
            .catch(function () { if (btn) btn.disabled = false; });

          return true;
        }

        return true;
      }

      function applyRule4ThenMaybeBlock(e, btn) {
        const drawer = document.getElementById("Cart-Drawer");
        const instance = window.cartDrawerInstance;

        if (!instance || !instance._isRule4Scenario || !instance._isRule4Scenario()) return false;

        const linesToRemove = instance._rule4LinesToRemove && instance._rule4LinesToRemove(drawer);
        if (!linesToRemove || linesToRemove.length === 0) return false;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (btn) btn.disabled = true;

        fetch("/cart.js")
          .then(function (res) { return res.json(); })
          .catch(function () { return null; })
          .then(function (cartData) {
            return instance._rule4ExpandWithAddons
              ? instance._rule4ExpandWithAddons(drawer, linesToRemove, cartData)
              : Promise.resolve(linesToRemove);
          })
          .then(function (expanded) {
            let idx = 0;

            function removeNext() {
              if (idx >= expanded.length) {
                window.location.href = checkoutUrl;
                return;
              }

              const line = expanded[idx];
              idx++;

              fetch("/cart.js")
                .then(function (r) { return r.json(); })
                .catch(function () { return { items: [] }; })
                .then(function (currentCart) {
                  const itemCount = currentCart && currentCart.items ? currentCart.items.length : 0;

                  if (line > itemCount) {
                    if (window._cartDrawerRule4PendingRemovalLines) window._cartDrawerRule4PendingRemovalLines.delete(line);
                    removeNext();
                    return;
                  }

                  return fetch(cartChangeUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    body: JSON.stringify({ line: line, quantity: 0 }),
                  })
                    .then(function (res) { return res.json ? res.json() : res.text(); })
                    .then(function () {
                      if (window._cartDrawerRule4PendingRemovalLines) window._cartDrawerRule4PendingRemovalLines.delete(line);
                      removeNext();
                    })
                    .catch(function () { if (btn) btn.disabled = false; });
                });
            }

            removeNext();
          })
          .catch(function () { if (btn) btn.disabled = false; });

        return true;
      }

      // Click guard for drawer checkout button
      document.addEventListener(
        "click",
        function (e) {
          const btn =
            e.target &&
            e.target.closest &&
            e.target.closest('form[data-cart-drawer-checkout-form] button[name="checkout"]');
          if (!btn) return;

          const drawer = document.getElementById("Cart-Drawer");
          if (!drawer || !drawer.contains(btn)) return;

          if (window.STORE_CONTEXT?.isShiels) {
            if (applyRule3ThenMaybeBlock(e, btn)) return;
            if (applyRule4ThenMaybeBlock(e, btn)) return;
          }

          const instance = window.cartDrawerInstance;
          if (!instance || !instance._shouldBlockCheckout || !instance._shouldBlockCheckout(drawer)) return;

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          instance._updateCheckoutButtonRule1Blocked && instance._updateCheckoutButtonRule1Blocked();
        },
        true
      );

      // Submit guard for drawer checkout form
      document.addEventListener(
        "submit",
        function (e) {
          if (!e.target || !e.target.hasAttribute("data-cart-drawer-checkout-form")) return;

          const drawer = document.getElementById("Cart-Drawer");
          if (!drawer || !drawer.contains(e.target)) return;

          const btn = e.target.querySelector('button[name="checkout"]');

          if (window.STORE_CONTEXT?.isShiels) {
            if (applyRule4ThenMaybeBlock(e, btn)) return;
            if (applyRule3ThenMaybeBlock(e, btn)) return;
          }

          const instance = window.cartDrawerInstance;
          if (!instance || !instance._shouldBlockCheckout) return;

          if (instance._shouldBlockCheckout(drawer)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            instance._updateCheckoutButtonRule1Blocked && instance._updateCheckoutButtonRule1Blocked();
          }
        },
        true
      );

      // Cart page submit guard
      document.addEventListener(
        "submit",
        function cartFormSubmitGuard(e) {
          if (!e.target || e.target.id !== "cart-form") return;

          const cartEl = document.getElementById("Cart");
          if (!cartEl) return;

          if (
            cartEl.classList.contains("cart-disabled") ||
            cartEl.querySelector(".cart__row.loading") ||
            cartEl.querySelector('[data-removing="true"]')
          ) {
            e.preventDefault();
            e.stopPropagation();
            window.cartDrawerInstance &&
              window.cartDrawerInstance._updateCheckoutButtonRule1Blocked &&
              window.cartDrawerInstance._updateCheckoutButtonRule1Blocked();
          }
        },
        true
      );

      // listen for custom events if theme dispatches them
      if (!window._cartLineItemChangeListenersBound) {
        window._cartLineItemChangeListenersBound = true;

        const updateButtons = function () {
          if (window.cartDrawerInstance && window.cartDrawerInstance._updateCheckoutButtonRule1Blocked) {
            window.cartDrawerInstance._updateCheckoutButtonRule1Blocked();
          }
        };

        document.addEventListener("line-item:change:start", updateButtons);
        document.addEventListener("line-item:change:end", updateButtons);

        if (this._updateCheckoutButtonRule1Blocked) this._updateCheckoutButtonRule1Blocked();
      }
    };

    return true;
  }

  // Install prototype patch now or wait for CartDrawer to exist
  (function waitForCartDrawerClassThenInstall() {
    if (installOnCartDrawerPrototype()) return;

    const startedAt = Date.now();
    const interval = setInterval(function () {
      if (installOnCartDrawerPrototype()) {
        clearInterval(interval);
        return;
      }
      // safety timeout (10s)
      if (Date.now() - startedAt > 10000) {
        clearInterval(interval);
      }
    }, 50);
  })();

  // ------------------------------------------------------------
  // 1) After prototype is patched, run setups when instance exists
  // ------------------------------------------------------------
  function runSetupsWhenInstanceExists() {
    if (!window.cartDrawerInstance) return false;
    try {
      window.cartDrawerInstance.setupDrawerDocumentDelegation &&
        window.cartDrawerInstance.setupDrawerDocumentDelegation();
      window.cartDrawerInstance.setupCheckoutSubmitGuard &&
        window.cartDrawerInstance.setupCheckoutSubmitGuard();
    } catch (e) {}
    return true;
  }

  (function waitForCartDrawerInstance() {
    if (runSetupsWhenInstanceExists()) return;

    const startedAt = Date.now();
    const interval = setInterval(function () {
      if (runSetupsWhenInstanceExists()) {
        clearInterval(interval);
        return;
      }
      // safety timeout (10s)
      if (Date.now() - startedAt > 10000) {
        clearInterval(interval);
      }
    }, 50);
  })();

  // ------------------------------------------------------------
  // 2) Shiels-only global /cart/add interceptor (once)
  // ------------------------------------------------------------
  if (window.STORE_CONTEXT?.isShiels) {
    window.ShielsEnhancements = window.ShielsEnhancements || {};

    window.ShielsEnhancements.setupGlobalCartAddRefresh = function () {
      if (window._globalCartAddRefreshInstalled) return;
      window._globalCartAddRefreshInstalled = true;

      const origFetch = window.fetch;

      window.fetch = function (...args) {
        const url = typeof args[0] === "string" ? args[0] : args[0] && args[0].url;
        const isCartAdd = url && String(url).indexOf("/cart/add") !== -1;

        return origFetch.apply(this, args).then(function (response) {
          if (isCartAdd && response && response.ok) {
            response
              .clone()
              .json()
              .then(function (data) {
                if (!data.status && window.cartDrawerInstance && typeof window.cartDrawerInstance.refresh === "function") {
                  document.body.classList.add("open-cc");

                  const drawer = document.getElementById("Cart-Drawer");
                  if (drawer) drawer.classList.add("active");

                  setTimeout(function () {
                    window.cartDrawerInstance.refresh();

                    if (window.cartDrawerInstance.hideQuantitySelectorForAddOns) {
                      window.cartDrawerInstance.hideQuantitySelectorForAddOns();
                    }
                    if (window.cartDrawerInstance.removeStrikethroughFromMainProducts) {
                      window.cartDrawerInstance.removeStrikethroughFromMainProducts();
                    }
                  }, 150);
                }
              })
              .catch(function () {});
          }

          return response;
        });
      };
    };

    window.ShielsEnhancements.setupGlobalCartAddRefresh();
  }
})();